import { useEffect, useState, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, formatApiError, uploadPhoto, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { CATEGORY_LABELS, STATUS_LABELS, STATUS_COLORS, SIZE_LABELS, TIME_LABELS } from "@/lib/constants";
import {
  ArrowLeft, MapPin, Calendar, Package, Clock, User as UserIcon,
  Loader2, CheckCircle, Gavel, AlertCircle, X, MessageCircle, Star,
  Camera, ThumbsUp, ShieldAlert, Download, Lock, Share2, Leaf, Truck,
} from "lucide-react";
import PaymentFlowInfo from "@/components/PaymentFlowInfo";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Polyline } from "react-leaflet";
import { generateReceiptPDF } from "@/lib/receipt";
import { useLang } from "@/lib/LanguageContext";

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [job, setJob] = useState(null);
  const [bids, setBids] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pollingPayment, setPollingPayment] = useState(false);

  const refetch = async () => {
    const [j, b, m] = await Promise.all([
      api.get(`/jobs/${id}`),
      api.get("/bids", { params: { job_id: id } }),
      api.get("/messages", { params: { job_id: id } }).catch(() => ({ data: [] })),
    ]);
    setJob(j.data);
    setBids(b.data);
    setMessages(m.data);
  };

  useEffect(() => {
    refetch().finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [id]);

  // Poll Stripe checkout status if returning from payment
  useEffect(() => {
    if (!sessionId) return;
    setPollingPayment(true);
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Betaling gennemført!");
          setSearchParams({});
          setPollingPayment(false);
          await refetch();
          return;
        }
        if (data.status === "expired") {
          toast.error("Betalingen udløb");
          setPollingPayment(false);
          return;
        }
      } catch {}
      if (++attempts < 8) setTimeout(poll, 2000);
      else setPollingPayment(false);
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-8"><Skeleton className="h-64 rounded-2xl" /></div>;
  if (!job) return <div className="max-w-4xl mx-auto px-4 py-20 text-center"><h2 className="text-xl font-semibold mb-2">Job ikke fundet</h2><Link to="/jobs"><Button variant="outline">Se jobs</Button></Link></div>;

  const isOwner = user?.email === job.creator_email;
  const isDriver = user?.email === job.accepted_by;
  const myBid = user && bids.find(b => b.driver_email === user.email);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <Link to="/jobs" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Tilbage til jobs
      </Link>

      {pollingPayment && (
        <Card className="mb-6 border-primary/30 bg-primary/5">
          <CardContent className="py-4 flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
            <p className="text-sm">Bekræfter betaling...</p>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {/* Job summary */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <CardTitle className="text-2xl mb-3">{job.title}</CardTitle>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary">{CATEGORY_LABELS[job.category]}</Badge>
                    <Badge variant="outline" className={STATUS_COLORS[job.status]}>{STATUS_LABELS[job.status]}</Badge>
                    {job.bike_friendly && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 gap-1" data-testid="job-bike-badge"><Leaf className="w-3 h-3" /> Kan hentes på ladcykel</Badge>}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-primary" data-testid="job-budget">{job.budget} DKK</p>
                  <p className="text-xs text-muted-foreground">Budget</p>
                  <Button
                    variant="outline" size="sm" className="mt-2 h-7 text-xs gap-1.5"
                    onClick={async () => {
                      const url = window.location.href;
                      if (navigator.share) {
                        try { await navigator.share({ title: job.title, text: `Kan du hente dette? "${job.title}", ${job.budget} kr. på Grabber`, url }); } catch {}
                      } else {
                        await navigator.clipboard.writeText(url);
                        toast.success("Link kopieret, del det med chauffører!");
                      }
                    }}
                    data-testid="share-job-btn"
                  >
                    <Share2 className="w-3 h-3" /> Del
                  </Button>
                </div>
              </div>
              <div className="mt-3">
                <PaymentFlowInfo />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {job.description && <p className="text-muted-foreground leading-relaxed">{job.description}</p>}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                <Info icon={<MapPin />} label="Afhentning" value={job.pickup_address} link={`https://maps.google.com/?q=${encodeURIComponent(job.pickup_address)}`} />
                {job.dropoff_address && <Info icon={<MapPin />} label="Aflevering" value={job.dropoff_address} link={`https://maps.google.com/?q=${encodeURIComponent(job.dropoff_address)}`} />}
                {job.preferred_date && <Info icon={<Calendar />} label="Dato" value={job.preferred_date} />}
                {job.preferred_time && <Info icon={<Clock />} label="Tidspunkt" value={TIME_LABELS[job.preferred_time]} />}
                {job.size_estimate && <Info icon={<Package />} label="Størrelse" value={SIZE_LABELS[job.size_estimate]} />}
                <Info icon={<UserIcon />} label="Oprettet af" value={job.poster_name || "Anonym"} />
              </div>
              {job.dimensions && <div className="text-sm pt-2"><p className="text-xs text-muted-foreground">Mål</p><p className="font-medium">{job.dimensions}</p></div>}
              {job.photos?.length > 0 && (
                <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
                  {job.photos.map((url, i) => (
                    <div key={i} className="w-24 h-24 rounded-xl overflow-hidden border border-border">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}

              {/* Driver start/completion photos */}
              {(job.start_photos?.length > 0 || job.completion_photos?.length > 0) && (
                <div className="pt-4 border-t border-border space-y-3">
                  {job.start_photos?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Startbilleder ({job.start_photos.length})</p>
                      <div className="flex flex-wrap gap-2">
                        {job.start_photos.map((p, i) => (
                          <a key={i} href={p.url || p} target="_blank" rel="noreferrer" className="w-20 h-20 rounded-lg overflow-hidden border border-border hover:opacity-90 transition-opacity">
                            <img src={p.url || p} alt="" className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {job.completion_photos?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">Slutbilleder ({job.completion_photos.length})</p>
                      <div className="flex flex-wrap gap-2">
                        {job.completion_photos.map((p, i) => (
                          <a key={i} href={p.url || p} target="_blank" rel="noreferrer" className="w-20 h-20 rounded-lg overflow-hidden border-2 border-primary/30 hover:opacity-90 transition-opacity">
                            <img src={p.url || p} alt="" className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Map preview */}
              {job.pickup_lat && job.pickup_lng && (
                <div className="rounded-xl overflow-hidden border border-border h-48 mt-2">
                  <MapContainer center={[job.pickup_lat, job.pickup_lng]} zoom={13} className="h-full w-full">
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
                    <Marker position={[job.pickup_lat, job.pickup_lng]} />
                  </MapContainer>
                </div>
              )}

              {/* Owner cancel */}
              {isOwner && job.status === "open" && (
                <div className="pt-4 border-t border-border">
                  <CancelJobButton jobId={id} onCancelled={refetch} />
                </div>
              )}

              {/* Driver status actions */}
              {isDriver && (job.status === "accepted" || job.status === "in_progress") && (
                <DriverJobActions job={job} onUpdated={refetch} />
              )}

              {/* Owner approval */}
              {isOwner && job.status === "awaiting_approval" && (
                <ApprovalPanel job={job} onApproved={refetch} />
              )}
            </CardContent>
          </Card>

          {/* Bids list */}
          <BidsCard bids={bids} job={job} isOwner={isOwner} refetch={refetch} />

          {/* Chat */}
          {user && (isOwner || isDriver || myBid) && (
            <JobChat jobId={id} messages={messages} onSent={refetch} currentUser={user} />
          )}

          {/* Review for completed */}
          {job.status === "completed" && isOwner && job.accepted_by && (
            <ReviewForm jobId={id} driverEmail={job.accepted_by} onReviewed={refetch} />
          )}

          {/* Driver reviews customer */}
          {job.status === "completed" && isDriver && (
            <CustomerReviewForm jobId={id} />
          )}

          {/* Receipt download */}
          {job.status === "completed" && (isOwner || isDriver) && (
            <ReceiptDownload jobId={id} />
          )}

          {/* GPS tracking - shown for owner+driver when in_progress */}
          {(job.status === "in_progress" || job.status === "accepted") && (isOwner || isDriver) && (
            <LiveLocationCard job={job} isDriver={isDriver} />
          )}
        </div>

        {/* Sidebar: bid form */}
        <div>
          {!user && job.status === "open" && (
            <Card className="sticky top-24">
              <CardContent className="py-6 text-center">
                <p className="text-sm text-muted-foreground mb-4">Log ind for at byde på dette job</p>
                <Link to="/login"><Button className="w-full">Log ind</Button></Link>
              </CardContent>
            </Card>
          )}
          {user && !isOwner && job.status === "open" && !myBid && !user.is_suspended && (
            user.is_driver ? (
              <BidForm jobId={id} budget={job.budget} onSubmitted={refetch} />
            ) : (
              <Card className="border-primary/20 bg-primary/5" data-testid="become-grabber-hook">
                <CardContent className="py-5 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold">Vil du tjene penge på at hente dette? 💰</p>
                    <p className="text-sm text-muted-foreground">Bliv grabber på 1 minut og byd på jobbet, du beholder 80%.</p>
                  </div>
                  <Link to="/bliv-grabber"><Button className="gap-2" data-testid="become-grabber-btn"><Truck className="w-4 h-4" /> Bliv grabber</Button></Link>
                </CardContent>
              </Card>
            )
          )}
          {myBid && job.status === "open" && (
            <Card className="sticky top-24">
              <CardContent className="py-6 text-center">
                <CheckCircle className="w-8 h-8 text-primary mx-auto mb-2" />
                <p className="font-semibold">Bud afgivet</p>
                <p className="text-sm text-muted-foreground mb-3">Venter på at opretteren accepterer.</p>
                <p className="text-xs">Dit bud: <span className="font-bold text-primary">{myBid.amount} DKK</span></p>
                <Button variant="outline" size="sm" className="mt-3" onClick={async () => {
                  if (!window.confirm("Træk dit bud tilbage?")) return;
                  await api.delete(`/bids/${myBid.id}`);
                  toast.success("Bud trukket tilbage");
                  refetch();
                }}>Træk bud tilbage</Button>
              </CardContent>
            </Card>
          )}
          {job.accepted_by_name && (
            <Card className="sticky top-24 mt-4">
              <CardContent className="py-6">
                <p className="text-xs text-muted-foreground mb-1">Accepteret chauffør</p>
                <Link to={`/driver/${encodeURIComponent(job.accepted_by)}`} className="font-semibold hover:text-primary">{job.accepted_by_name}</Link>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Info({ icon, label, value, link }) {
  const content = link ? <a href={link} target="_blank" rel="noreferrer" className="font-medium text-primary hover:underline">{value}</a> : <p className="font-medium">{value}</p>;
  return (
    <div className="flex items-start gap-2 text-sm">
      <div className="text-muted-foreground mt-0.5 [&>svg]:w-4 [&>svg]:h-4">{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        {content}
      </div>
    </div>
  );
}

function BidsCard({ bids, job, isOwner, refetch }) {
  const [greenOnly, setGreenOnly] = useState(false);
  const isGreen = (b) => /cykel/i.test(b.driver_vehicle || "");
  const greenCount = bids.filter(isGreen).length;
  const shown = greenOnly ? bids.filter(isGreen) : bids;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-lg">Bud ({bids.length})</CardTitle>
        {greenCount > 0 && (
          <button
            onClick={() => setGreenOnly(!greenOnly)}
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${greenOnly ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-background text-muted-foreground border-border hover:border-emerald-300 hover:text-emerald-700"}`}
            data-testid="green-filter-toggle"
          >
            <Leaf className="w-3.5 h-3.5" /> Kun grøn transport ({greenCount})
          </button>
        )}
      </CardHeader>
      <CardContent>
        {shown.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            {greenOnly ? "Ingen bud med grøn transport endnu" : "Ingen bud endnu. Vær den første!"}
          </p>
        ) : (
          <div className="space-y-3">
            {shown.map(bid => <BidItem key={bid.id} bid={bid} job={job} isOwner={isOwner} onAccept={refetch} />)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BidItem({ bid, job, isOwner, onAccept }) {
  const [accepting, setAccepting] = useState(false);
  const [credit, setCredit] = useState(0);
  const [availableCredit, setAvailableCredit] = useState(0);
  const [useCredit, setUseCredit] = useState(false);

  useEffect(() => {
    if (isOwner && job.status === "open" && bid.status === "pending") {
      api.get("/referrals/me").then(r => {
        const bal = r.data.credit_balance || 0;
        setAvailableCredit(bal);
        if (bal > 0) {
          setCredit(Math.min(bal, Math.max(0, bid.amount - 1)));
        }
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [isOwner, job.status, bid.status]);

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post("/checkout/create", {
        job_id: bid.job_id,
        bid_id: bid.id,
        origin_url: window.location.origin,
        use_credit: useCredit ? credit : 0,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Kunne ikke oprette betaling");
      setAccepting(false);
    }
  };

  const showAccept = isOwner && job.status === "open" && bid.status === "pending";

  return (
    <div className="rounded-xl border border-border p-4 hover:border-primary/30 transition-colors">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <Link to={`/driver/${encodeURIComponent(bid.driver_email)}`} className="font-semibold hover:text-primary hover:underline">{bid.driver_name || "Chauffør"}</Link>
          {bid.driver_vehicle && (
            /cykel/i.test(bid.driver_vehicle) ? (
              <Badge className="ml-2 text-[11px] bg-emerald-100 text-emerald-800 hover:bg-emerald-100 gap-1" data-testid="bid-vehicle-badge">
                <Leaf className="w-3 h-3" /> {bid.driver_vehicle}, grøn transport
              </Badge>
            ) : (
              <Badge variant="secondary" className="ml-2 text-[11px] gap-1" data-testid="bid-vehicle-badge">
                <Truck className="w-3 h-3" /> {bid.driver_vehicle}
              </Badge>
            )
          )}
          {bid.message && <p className="text-sm text-muted-foreground mt-2">{bid.message}</p>}
          {bid.estimated_arrival && <p className="text-xs text-muted-foreground mt-1">ETA: {bid.estimated_arrival}</p>}
        </div>
        <div className="text-right">
          <p className="text-xl font-bold text-primary">{bid.amount} DKK</p>
          <Badge variant="outline" className="mt-1 text-xs">{bid.status}</Badge>
        </div>
      </div>
      {showAccept && (
        <>
          {availableCredit > 0 && (
            <label className="flex items-start gap-2 mt-3 p-3 rounded-lg bg-primary/5 border border-primary/20 cursor-pointer">
              <input
                type="checkbox"
                checked={useCredit}
                onChange={(e) => setUseCredit(e.target.checked)}
                className="mt-0.5"
                data-testid="use-credit-checkbox"
              />
              <div className="text-sm">
                <p className="font-medium">Brug saldo ({availableCredit.toFixed(2)} kr.)</p>
                <p className="text-xs text-muted-foreground">
                  {useCredit ? `Du betaler ${Math.max(1, bid.amount - credit).toFixed(2)} kr. og bruger ${credit.toFixed(2)} kr. fra saldo` : "Klik for at bruge din saldo som rabat"}
                </p>
              </div>
            </label>
          )}
          <Button size="sm" className="mt-3 gap-2" onClick={accept} disabled={accepting} data-testid={`accept-bid-${bid.id}`}>
            {accepting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Acceptér & betal {useCredit && credit > 0 ? `${Math.max(1, bid.amount - credit).toFixed(0)} DKK` : ""}
          </Button>
          <div className="mt-3 p-3 rounded-lg bg-muted/60 border border-border flex items-start gap-2.5" data-testid="escrow-info-box">
            <Lock className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-semibold text-foreground">Tryg betaling:</span> Din betaling opbevares sikkert hos Grabber og udbetales <span className="font-semibold text-foreground">først</span> til chaufføren, når du har godkendt det udførte job. Går noget galt, kan du åbne en tvist og få pengene refunderet.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function BidForm({ jobId, budget, onSubmitted }) {
  const [amount, setAmount] = useState(budget);
  const [message, setMessage] = useState("");
  const [eta, setEta] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Moderate message first
      if (message) {
        const { data } = await api.post("/messages/validate", { text: message });
        if (!data.valid) {
          toast.error(data.error || "Beskeden indeholder ikke-tilladt indhold");
          setLoading(false);
          return;
        }
      }
      await api.post("/bids", { job_id: jobId, amount: parseFloat(amount), message, estimated_arrival: eta });
      toast.success("Bud afgivet!");
      onSubmitted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Kunne ikke afgive bud");
    } finally { setLoading(false); }
  };

  return (
    <Card className="sticky top-24 border-primary/30 shadow-md shadow-primary/5">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-primary animate-pulse" /> Afgiv et bud</CardTitle>
        <p className="text-sm text-muted-foreground">Budget: <span className="font-semibold text-foreground">{budget} DKK</span></p>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Din pris (kr.)</Label>
            <Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid="bid-amount-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Besked til kunden</Label>
            <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="f.eks. Jeg kan hente det i dag inden kl. 18" data-testid="bid-message-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Forventet ankomst</Label>
            <Input value={eta} onChange={(e) => setEta(e.target.value)} placeholder="f.eks. Inden for 2 timer" />
          </div>
          <Button type="submit" className="w-full rounded-xl font-semibold" disabled={loading} data-testid="submit-bid-btn">
            {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Indsend bud
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function CancelJobButton({ jobId, onCancelled }) {
  return (
    <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/5" onClick={async () => {
      if (!window.confirm("Annuller dette job?")) return;
      await api.post(`/jobs/${jobId}/cancel`);
      toast.success("Job annulleret");
      onCancelled();
    }}>
      <X className="w-4 h-4 mr-2" /> Annuller job
    </Button>
  );
}

function DriverJobActions({ job, onUpdated }) {
  const [updating, setUpdating] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      if (file.size > 8_000_000) { toast.error(`${file.name} er for stor (max 8MB)`); continue; }
      try {
        const url = await uploadPhoto(file);
        setPhotos(p => [...p, url]);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || `Kunne ikke uploade ${file.name}`);
      }
    }
    setUploading(false);
  };

  const startJob = async () => {
    if (photos.length === 0) {
      toast.error("Upload mindst ét startbillede");
      return;
    }
    setUpdating(true);
    try {
      await api.post(`/jobs/${job.id}/start-photos`, { photos });
      toast.success("Job startet, startbilleder gemt");
      setShowUpload(false);
      setPhotos([]);
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Kunne ikke starte");
    } finally { setUpdating(false); }
  };

  const completeJob = async () => {
    if (photos.length === 0) {
      toast.error("Upload mindst ét slutbillede som bevis");
      return;
    }
    setUpdating(true);
    try {
      await api.post(`/jobs/${job.id}/completion-photos`, { photos });
      toast.success("Markeret færdig, kunden skal nu godkende");
      setShowUpload(false);
      setPhotos([]);
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Kunne ikke afslutte");
    } finally { setUpdating(false); }
  };

  if (!showUpload) {
    return (
      <div className="pt-4 border-t border-border space-y-2">
        {job.status === "accepted" && (
          <Button onClick={() => setShowUpload(true)} className="w-full gap-2"><Camera className="w-4 h-4" /> Start job (upload startbilleder)</Button>
        )}
        {job.status === "in_progress" && (
          <Button onClick={() => setShowUpload(true)} className="w-full gap-2"><CheckCircle className="w-4 h-4" /> Markér færdig (upload slutbilleder)</Button>
        )}
      </div>
    );
  }

  const isComplete = job.status === "in_progress";

  return (
    <div className="pt-4 border-t border-border">
      <div className="rounded-xl bg-muted/40 p-4">
        <p className="font-semibold text-sm mb-2 flex items-center gap-2">
          <Camera className="w-4 h-4 text-primary" />
          {isComplete ? "Upload slutbilleder" : "Upload startbilleder"}
        </p>
        <p className="text-xs text-muted-foreground mb-3">
          {isComplete
            ? "Bevis for at jobbet er udført korrekt. Anbefalet 2-4 fotos."
            : "Dokumentér tilstand før afhentning. Anbefalet 2-3 fotos."}
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {photos.map((p, i) => (
            <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border border-border group">
              <img src={p} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute top-1 right-1 w-5 h-5 bg-background/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          <label className="w-20 h-20 rounded-xl border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors" data-testid="job-photo-upload">
            {uploading ? <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" /> : <Camera className="w-5 h-5 text-muted-foreground" />}
            <input type="file" accept="image/*" multiple className="hidden" onChange={handleFile} disabled={uploading} />
          </label>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { setShowUpload(false); setPhotos([]); }}>Annuller</Button>
          <Button size="sm" onClick={isComplete ? completeJob : startJob} disabled={updating || uploading || photos.length === 0} data-testid="job-action-submit" className="gap-2">
            {updating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {isComplete ? "Indsend slutbilleder" : "Indsend startbilleder"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ApprovalPanel({ job, onApproved }) {
  const [loading, setLoading] = useState(false);
  const approve = async () => {
    setLoading(true);
    try {
      await api.post(`/jobs/${job.id}/approve`);
      toast.success("Job godkendt! Chaufføren får udbetalt.");
      onApproved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="pt-4 border-t border-border bg-amber-50/40 -mx-6 -mb-6 px-6 pb-6 rounded-b-xl">
      <div className="flex items-start gap-3 mb-4">
        <ThumbsUp className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
        <div>
          <p className="font-semibold">Chaufføren har markeret jobbet som færdigt</p>
          <p className="text-sm text-muted-foreground">Godkend for at frigive betalingen til chaufføren.</p>
        </div>
      </div>
      <Button onClick={approve} disabled={loading} className="w-full gap-2" data-testid="approve-job-btn">
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        <CheckCircle className="w-4 h-4" /> Godkend & frigør betaling
      </Button>
    </div>
  );
}

function JobChat({ jobId, messages, onSent, currentUser }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post("/messages", { job_id: jobId, text });
      setText("");
      onSent();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Kunne ikke sende besked");
    } finally { setSending(false); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-lg flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Chat</CardTitle></CardHeader>
      <CardContent>
        <div className="max-h-64 overflow-y-auto space-y-2 mb-3 pr-1">
          {messages.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Ingen beskeder endnu</p>}
          {messages.map(m => {
            const mine = m.sender_email === currentUser.email;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                  {!mine && <p className="text-xs opacity-70 mb-0.5">{m.sender_name}</p>}
                  <p>{m.text}</p>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
        <form onSubmit={send} className="flex gap-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Skriv en besked..." disabled={sending} data-testid="chat-input" />
          <Button type="submit" disabled={sending || !text.trim()} data-testid="chat-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send"}
          </Button>
        </form>
        <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> AI overvåger chat for at beskytte begge parter</p>
      </CardContent>
    </Card>
  );
}
function ReceiptDownload({ jobId }) {
  const [loading, setLoading] = useState(false);
  const { lang } = useLang();
  const download = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/jobs/${jobId}/receipt`);
      generateReceiptPDF(data, lang);
      toast.success(lang === "en" ? "Receipt downloaded" : "Kvittering downloadet");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Kunne ikke generere kvittering");
    } finally { setLoading(false); }
  };
  return (
    <Card>
      <CardContent className="py-5 flex items-center justify-between">
        <div>
          <p className="font-semibold">Kvittering klar</p>
          <p className="text-sm text-muted-foreground">Download PDF med jobdetaljer og betalingsoplysninger</p>
        </div>
        <Button onClick={download} disabled={loading} variant="outline" className="gap-2" data-testid="download-receipt-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Download
        </Button>
      </CardContent>
    </Card>
  );
}

function ReviewForm({ jobId, driverEmail, onReviewed }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/reviews", { job_id: jobId, rating, comment });
      toast.success("Tak for din anmeldelse!");
      setSubmitted(true);
      onReviewed();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (submitted) return null;

  return (
    <Card>
      <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Star className="w-4 h-4" /> Vurder chaufføren</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-3">
          <div className="flex gap-1">
            {[1,2,3,4,5].map(n => (
              <button type="button" key={n} onClick={() => setRating(n)} className="p-1">
                <Star className={`w-7 h-7 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
              </button>
            ))}
          </div>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Skriv en kort kommentar..." rows={3} />
          <Button type="submit" disabled={loading}>{loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Indsend</Button>
        </form>
      </CardContent>
    </Card>
  );
}


// ============== GPS Live tracking ==============
function LiveLocationCard({ job, isDriver }) {
  const [position, setPosition] = useState(null);
  const [track, setTrack] = useState([]);
  const [tracking, setTracking] = useState(false);
  const [wsState, setWsState] = useState("disconnected");
  const wsRef = useRef(null);
  const watchIdRef = useRef(null);

  useEffect(() => {
    // Load track history
    api.get(`/jobs/${job.id}/gps-track`).then(r => setTrack(r.data.track || [])).catch(() => {});
    const token = localStorage.getItem("grabber_token");
    if (!token) return;
    const wsUrl = API_BASE.replace(/^http/, "ws") + `/ws/jobs/${job.id}/location?token=${token}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch { return; }
    wsRef.current = ws;
    ws.onopen = () => setWsState("connected");
    ws.onclose = () => setWsState("disconnected");
    ws.onerror = () => setWsState("error");
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === "position") {
          setPosition(data);
          setTrack(t => [...t, { lat: data.lat, lng: data.lng, ts: data.ts }]);
        }
      } catch {}
    };
    return () => { try { ws.close(); } catch {} };
    // eslint-disable-next-line
  }, [job.id]);

  const startTracking = () => {
    if (!navigator.geolocation) { toast.error("Geolokation ikke understøttet"); return; }
    setTracking(true);
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const payload = { type: "position", lat: pos.coords.latitude, lng: pos.coords.longitude };
        if (wsRef.current && wsRef.current.readyState === 1) wsRef.current.send(JSON.stringify(payload));
      },
      (err) => { console.warn(err); toast.error("Kunne ikke hente position"); setTracking(false); },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 12000 }
    );
  };
  const stopTracking = () => {
    if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
    watchIdRef.current = null;
    setTracking(false);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${wsState === "connected" ? "bg-emerald-500 animate-pulse" : "bg-muted-foreground/40"}`} />
          Live position
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isDriver ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Del din position med kunden mens du er på vej.</p>
            {!tracking ? (
              <Button onClick={startTracking} className="gap-2" data-testid="gps-start-btn">
                <MapPin className="w-4 h-4" /> Start position-deling
              </Button>
            ) : (
              <Button onClick={stopTracking} variant="outline" className="gap-2 border-destructive/30 text-destructive" data-testid="gps-stop-btn">
                <X className="w-4 h-4" /> Stop deling
              </Button>
            )}
            {position && (
              <p className="text-xs text-muted-foreground">Sidst delt: {new Date(position.ts).toLocaleTimeString("da-DK")}</p>
            )}
          </div>
        ) : (
          <div>
            {(position || track.length > 0) ? (
              <>
                <div className="rounded-xl overflow-hidden border border-border h-64 mb-2">
                  <MapContainer
                    center={position ? [position.lat, position.lng] : [track[track.length-1].lat, track[track.length-1].lng]}
                    zoom={14}
                    className="h-full w-full"
                    key={position ? `${position.lat}-${position.lng}` : track.length}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OSM" />
                    {track.length > 1 && (
                      <Polyline
                        positions={track.map(t => [t.lat, t.lng])}
                        pathOptions={{ color: "#386d54", weight: 4, opacity: 0.7 }}
                      />
                    )}
                    {position && <Marker position={[position.lat, position.lng]} />}
                    {job.pickup_lat && job.pickup_lng && <Marker position={[job.pickup_lat, job.pickup_lng]} />}
                  </MapContainer>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{track.length} positions-punkter</span>
                  {position && <span>Sidst set {new Date(position.ts).toLocaleTimeString("da-DK")}</span>}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Chaufføren har endnu ikke delt sin position.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============== Customer review (driver -> customer) ==============
function CustomerReviewForm({ jobId }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/customer-reviews", { job_id: jobId, rating, comment });
      toast.success("Tak for din vurdering af kunden!");
      setSubmitted(true);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail);
      if (msg && msg.includes("allerede")) setSubmitted(true);
      else toast.error(msg);
    } finally { setLoading(false); }
  };

  if (submitted) return null;

  return (
    <Card>
      <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Star className="w-4 h-4" /> Vurder kunden</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-3">
          <div className="flex gap-1">
            {[1,2,3,4,5].map(n => (
              <button type="button" key={n} onClick={() => setRating(n)} className="p-1">
                <Star className={`w-7 h-7 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
              </button>
            ))}
          </div>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Hvordan var kunden? Klar, venlig, korrekt info..." rows={3} data-testid="customer-review-comment" />
          <Button type="submit" disabled={loading} data-testid="customer-review-submit">
            {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Indsend anmeldelse
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
